import Config from "../../../Config";
import { authHeaderLicman } from "../../../helpers/requestHelpers";

const config = new Config();

/** Default page size for usage history (must match API cap expectations). */
export const DEFAULT_PAGE_SIZE = 25;

export const EM_DASH = "—";

async function parseJsonResponse(response, text) {
    let data;
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(text || "Ungültige Antwort");
    }
    if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
    }
    return data;
}

/**
 * GET licman quota balances summary for a license (current stand per quota definition).
 *
 * @param {number} licenseId
 * @returns {Promise<Object>}
 */
export async function fetchQuotaSummary(licenseId) {
    const search = new URLSearchParams();
    search.set("licenseId", String(licenseId));
    const response = await fetch(
        `${config.apiUrl}/${config.licmanModule}/getLicenseQuotaSummary?${search.toString()}`,
        {
            method: "GET",
            headers: authHeaderLicman(),
        }
    );
    const text = await response.text();
    return parseJsonResponse(response, text);
}

/**
 * GET paginated QuotaUsages for one or more quota definition ids.
 *
 * @param {Object} params
 * @param {number} params.licenseId
 * @param {number[]} params.quotaIds
 * @param {number} [params.page] - 1-based
 * @param {number} [params.pageSize]
 * @returns {Promise<Object>}
 */
export async function fetchQuotaUsageHistory({ licenseId, quotaIds, page, pageSize }) {
    const search = new URLSearchParams();
    search.set("licenseId", String(licenseId));
    search.set("quotaIds", quotaIds.join(","));
    search.set("page", String(page ?? 1));
    search.set("pageSize", String(pageSize ?? DEFAULT_PAGE_SIZE));
    const response = await fetch(
        `${config.apiUrl}/${config.licmanModule}/getLicenseQuotaUsageHistory?${search.toString()}`,
        {
            method: "GET",
            headers: authHeaderLicman(),
        }
    );
    const text = await response.text();
    return parseJsonResponse(response, text);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatAmount(value) {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
        return EM_DASH;
    }
    return Number(value).toLocaleString("de-DE", { maximumFractionDigits: 4 });
}

/**
 * @param {string | null | undefined} isoInstant
 * @returns {string}
 */
export function formatOccurredAt(isoInstant) {
    if (!isoInstant || typeof isoInstant !== "string") {
        return EM_DASH;
    }
    const d = new Date(isoInstant);
    if (Number.isNaN(d.getTime())) {
        return EM_DASH;
    }
    return new Intl.DateTimeFormat("de-DE", {
        dateStyle: "short",
        timeStyle: "medium",
        timeZone: "Europe/Berlin",
    }).format(d);
}

/**
 * Human-readable label for quota definition type or usage type strings from the API.
 *
 * @param {string | null | undefined} quotaType
 * @returns {string}
 */
export function formatQuotaTypeLabel(quotaType) {
    const type = (quotaType || "").toLowerCase();
    if (type.includes("included")) {
        return "Inklusivkontingent";
    }
    if (type.includes("extra")) {
        return "Zusatzkontingent";
    }
    return "Verbrauch";
}

/**
 * @param {string | null | undefined} unit
 * @returns {string}
 */
export function formatUnitLabel(unit) {
    switch (unit) {
        case "minutes":
            return "Minuten";
        case "orders":
            return "Bestellungen";
        case "posts":
            return "Social Media Beiträge";
        case "messages":
            return "Nachrichten";
        default:
            return unit || EM_DASH;
    }
}

/**
 * @param {number[]|null|undefined} a
 * @param {number[]|null|undefined} b
 * @returns {boolean}
 */
export function quotaIdsEqual(a, b) {
    const sa = a?.length ? [...a].sort((x, y) => x - y).join(",") : "";
    const sb = b?.length ? [...b].sort((x, y) => x - y).join(",") : "";
    return sa === sb && sa !== "";
}

/**
 * Label for the history panel from a grouped summary row (module · unit).
 *
 * @param {{ moduleName: string|null; unit: string|null; quotaIds: number[] }} row
 * @returns {string}
 */
export function getHistoryPanelLabel(row) {
    const parts = [];
    if (row.moduleName?.trim()) {
        parts.push(row.moduleName.trim());
    }
    const unitLabel = formatUnitLabel(row.unit);
    if (unitLabel && unitLabel !== EM_DASH) {
        parts.push(unitLabel);
    }
    if (parts.length > 0) {
        return parts.join(" · ");
    }
    return `Kontingente ${row.quotaIds?.join(", ") ?? ""}`;
}

/**
 * @param {{ moduleName: string|null; quotaIds: number[] }} row
 * @returns {string}
 */
export function getModuleCellText(row) {
    if (row.moduleName) {
        return row.moduleName;
    }
    if (row.quotaIds?.length) {
        return `Quota ${row.quotaIds.join(", ")}`;
    }
    return EM_DASH;
}

/**
 * Groups API summary rows by module name and unit and sums balance (all quota definitions merged).
 *
 * @param {Array<{
 *   quotaIds: number[];
 *   moduleName: string|null;
 *   unit: string|null;
 *   balanceTotal: number;
 * }>|undefined|null} quotaSummaries
 * @returns {Array<{
 *   groupKey: string;
 *   moduleName: string|null;
 *   unit: string|null;
 *   balanceTotal: number;
 *   quotaIds: number[];
 * }>}
 */
export function aggregateSummariesByModuleAndUnit(quotaSummaries) {
    if (!Array.isArray(quotaSummaries) || quotaSummaries.length === 0) {
        return [];
    }
    /** @type {Map<string, { moduleName: string|null; unit: string|null; balanceTotal: number; quotaIds: number[] }>} */
    const map = new Map();
    for (const row of quotaSummaries) {
        const moduleName = row.moduleName ?? null;
        const unit = row.unit ?? null;
        const groupKey = `${moduleName ?? ""}\u0001${unit ?? ""}`;
        if (!map.has(groupKey)) {
            map.set(groupKey, {
                moduleName,
                unit,
                balanceTotal: 0,
                quotaIds: [],
            });
        }
        const g = map.get(groupKey);
        g.balanceTotal += Number(row.balanceTotal) || 0;
        g.quotaIds.push(...(row.quotaIds || []));
    }
    const out = [];
    for (const [groupKey, g] of map) {
        out.push({
            groupKey,
            moduleName: g.moduleName,
            unit: g.unit,
            balanceTotal: g.balanceTotal,
            quotaIds: [...new Set(g.quotaIds)].sort((a, b) => a - b),
        });
    }
    out.sort((a, b) => {
        const byName = (a.moduleName || "").localeCompare(b.moduleName || "", "de");
        if (byName !== 0) {
            return byName;
        }
        return (a.unit || "").localeCompare(b.unit || "", "de");
    });
    return out;
}
